import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useIsMutating, useMutationState } from '@tanstack/react-query';
import { ScreenLoader } from '../components/ScreenLoader';

type ManualLoading = {
  message: string;
  depth: number;
};

type LoadingOverlayContextValue = {
  setManualLoading: (active: boolean, message?: string) => void;
};

const LoadingOverlayContext = createContext<LoadingOverlayContextValue | null>(null);

export function LoadingOverlayProvider({ children }: { children: ReactNode }) {
  const [manual, setManual] = useState<ManualLoading | null>(null);
  const manualMessageRef = useRef('Please wait…');

  const setManualLoading = useCallback((active: boolean, message = 'Please wait…') => {
    if (active) {
      manualMessageRef.current = message;
      setManual((prev) => ({
        message,
        depth: (prev?.depth ?? 0) + 1,
      }));
      return;
    }
    setManual((prev) => {
      if (!prev) return null;
      const nextDepth = prev.depth - 1;
      return nextDepth > 0 ? { ...prev, depth: nextDepth, message: manualMessageRef.current } : null;
    });
  }, []);

  const value = useMemo(() => ({ setManualLoading }), [setManualLoading]);

  return (
    <LoadingOverlayContext.Provider value={value}>
      {children}
      <GlobalLoadingOverlay manual={manual} />
    </LoadingOverlayContext.Provider>
  );
}

function useMutationLoadingMessage(): string | undefined {
  const pending = useMutationState({
    filters: { status: 'pending' },
    select: (mutation) => ({
      message: mutation.options.meta?.loadingMessage as string | undefined,
      hidden: Boolean(mutation.options.meta?.noScreenLoader),
    }),
  });
  return pending.find((m) => !m.hidden && m.message)?.message;
}

function GlobalLoadingOverlay({ manual }: { manual: ManualLoading | null }) {
  const mutatingCount = useIsMutating({
    predicate: (mutation) => !mutation.options.meta?.noScreenLoader,
  });
  const mutationMessage = useMutationLoadingMessage();
  const visible = Boolean(manual) || mutatingCount > 0;
  if (!visible) return null;

  const message = manual?.message ?? mutationMessage ?? 'Please wait…';
  return <ScreenLoader message={message} />;
}

export function useLoadingOverlay() {
  const ctx = useContext(LoadingOverlayContext);
  if (!ctx) {
    throw new Error('useLoadingOverlay must be used within LoadingOverlayProvider');
  }
  return ctx;
}

/** Tie local async state to the global full-screen loader. */
export function useManualScreenLoading(active: boolean, message = 'Please wait…') {
  const { setManualLoading } = useLoadingOverlay();
  useEffect(() => {
    setManualLoading(active, message);
    return () => setManualLoading(false);
  }, [active, message, setManualLoading]);
}
